import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Headphones, BookOpen, PenLine, Mic, Sparkles, ArrowRight, Zap,
  ShieldCheck, Target, Brain, Timer, MessageSquareQuote,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import { Button } from '@/components/ui/button';

// Phase 1C canonical routes — the completed IELTS V2 experience.
// The old /ielts/practice, /ielts/mock-test, /ielts/writing, /ielts/speaking
// legacy paths remain live but are NO LONGER surfaced by these CTAs.
const V2_HUB = '/resources/ielts';
const V2_MOCK = '/resources/ielts/mock';
const V2_WRITING = '/resources/ielts/writing';
const V2_SPEAKING = '/resources/ielts/speaking';

const SECTIONS_INFO = [
  { key: 'listening', icon: Headphones, title: 'Listening', meta: '40 questions · 30 min',
    desc: 'Conversations, monologues, lectures and discussions across four parts.', link: `${V2_HUB}?skill=listening` },
  { key: 'reading', icon: BookOpen, title: 'Reading', meta: '40 questions · 60 min',
    desc: 'Three passages with matching, true/false/not-given, and short-answer formats.', link: `${V2_HUB}?skill=reading` },
  { key: 'writing', icon: PenLine, title: 'Writing', meta: '2 tasks · 60 min',
    desc: 'Task 1 (report/letter) + Task 2 (essay) — live word count, autosave & AI band feedback.', link: V2_WRITING },
  { key: 'speaking', icon: Mic, title: 'Speaking', meta: '3 parts · 11–14 min',
    desc: 'Interview, cue card, and discussion — record into your mic with prep timers and AI band feedback.', link: V2_SPEAKING },
];

const FEATURES = [
  { icon: Brain, title: 'Adaptive Learning', desc: 'Difficulty self-tunes after every answer so you spend time only on what raises your band.' },
  { icon: Timer, title: 'Full Test Simulation', desc: 'All four IELTS sections, exam-accurate timings and structure.' },
  {
    icon: MessageSquareQuote,
    title: 'Winhouse AI Feedback',
    desc: 'Submit Writing & Speaking — get instant Claude-powered band scores with sentence-level edits, vocabulary upgrades and a Band-8 sample.',
    badge: 'Live',
    ctaLabel: 'Submit Writing or Speaking for instant band feedback →',
    ctaTo: V2_WRITING,
  },
];

const TRUST_POINTS = [
  { icon: ShieldCheck, label: 'Academic + General' },
  { icon: Target, label: 'Band 0–9 estimate' },
  { icon: Zap, label: 'Instant explanations' },
];

const IeltsLandingPage = () => {
  const [mobile, setMobile] = useState(false);
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="ielts-landing">
      <Seo title="IELTS Prep · Winhouse Tutors" canonical="/ielts"
        description="AI-powered adaptive IELTS preparation for Academic and General — original questions, full mock tests, instant explanations." />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <FloatingActionButtons />

      <main className="flex-1">
        {/* HERO */}
        <section className="relative overflow-hidden bg-black text-white">
          <div className="absolute inset-0 opacity-90" style={{
            background: 'radial-gradient(60% 50% at 20% 0%, rgba(220,38,38,0.35) 0%, rgba(220,38,38,0) 60%), radial-gradient(50% 40% at 85% 20%, rgba(211,216,111,0.18) 0%, rgba(211,216,111,0) 70%), linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
          }} />
          <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-24 sm:pt-36 sm:pb-32">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur rounded-full px-3 py-1 text-xs uppercase tracking-[0.18em] mb-6">
                <Sparkles className="h-3.5 w-3.5 text-[#D3D86F]" /> Winhouse IELTS AI Learning
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
                AI-Powered IELTS Prep
                <span className="block text-[#D3D86F]">by Winhouse Tutors</span>
              </h1>
              <p className="text-lg sm:text-xl text-white/75 max-w-2xl mt-6 leading-relaxed">
                Adaptive practice across all four sections. Track your band, target your weakest skill,
                and walk in confident — for Academic <em>or</em> General Training.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 bg-[#D3D86F]/10 border border-[#D3D86F]/30 rounded-full pl-3 pr-4 py-2 text-sm sm:text-base">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#D3D86F] text-black flex-shrink-0">
                  <Zap className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <span className="text-white font-semibold">Free Diagnostic</span>
                <span className="text-white/50">+</span>
                <span className="text-white font-semibold">Full Mock Tests</span>
                <span className="text-[#D3D86F] hidden sm:inline">Available</span>
              </div>

              <div className="flex flex-wrap gap-3 mt-9">
                <Button onClick={() => nav(V2_HUB)}
                  className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-7 h-12 text-base rounded-full"
                  data-testid="ielts-cta-start">
                  Start Practice <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button onClick={() => nav(V2_MOCK)}
                  variant="outline"
                  className="bg-white/5 backdrop-blur border-white/25 text-white hover:bg-white/15 hover:text-white px-7 h-12 text-base rounded-full"
                  data-testid="ielts-cta-diagnostic">
                  Take Diagnostic Test
                </Button>
                <Button onClick={() => nav(V2_MOCK)}
                  variant="outline"
                  className="bg-[#D3D86F]/10 border-[#D3D86F]/40 text-[#D3D86F] hover:bg-[#D3D86F]/25 hover:text-white px-7 h-12 text-base rounded-full"
                  data-testid="ielts-cta-full-mock">
                  Full Mock Tests <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Link to="/get-started">
                  <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 px-5 h-12 text-base rounded-full"
                    data-testid="ielts-cta-program">
                    Join IELTS Program →
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-x-7 gap-y-3 mt-10 text-sm text-white/70">
                {TRUST_POINTS.map(({ icon: Icon, label }) => (
                  <span key={label} className="inline-flex items-center gap-2">
                    <Icon className="h-4 w-4 text-[#D3D86F]" /> {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Why Winhouse IELTS</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Built for serious IELTS candidates.</h2>
            <p className="text-gray-600 mt-3 text-base">Three things separate a Band 6 from a Band 8 — we engineered the platform around all three.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc, badge, ctaLabel, ctaTo }) => (
              <div key={title} className="bg-white border border-gray-200 rounded-2xl p-7 hover:border-black hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all flex flex-col"
                data-testid={`ielts-feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center">
                    <Icon className="h-5 w-5 text-[#D3D86F]" />
                  </div>
                  {badge ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full bg-[#D3D86F]/30 text-black border border-[#D3D86F]"
                      data-testid="ielts-feature-badge">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-700 animate-pulse" /> {badge}
                    </span>
                  ) : null}
                </div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed flex-1">{desc}</p>
                {ctaTo ? (
                  <Link to={ctaTo}
                    className="mt-5 inline-flex items-center text-sm font-semibold text-red-700 hover:text-black transition-colors"
                    data-testid="ielts-feature-cta">
                    {ctaLabel}
                  </Link>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        {/* SECTIONS */}
        <section className="bg-[#FAF9F6] py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Four sections · One platform</div>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Coverage that mirrors the real test.</h2>
              </div>
              <Button onClick={() => nav(V2_HUB)}
                variant="outline" className="rounded-full border-black text-black hover:bg-black hover:text-white">
                Start now <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {SECTIONS_INFO.map(({ key, icon: Icon, title, desc, meta, link }) => (
                <Link key={key} to={link}
                  className="block bg-white rounded-2xl border border-gray-200 p-6 hover:border-red-600 hover:-translate-y-0.5 transition-all"
                  data-testid={`ielts-section-${key}`}>
                  <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-red-700" />
                  </div>
                  <div className="text-xs uppercase tracking-wider text-gray-400 mb-1.5">{meta}</div>
                  <h3 className="font-bold text-lg mb-2">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-black text-white">
          <div className="max-w-4xl mx-auto px-6 py-20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Every question is an <span className="text-[#D3D86F]">original</span>.
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8 text-base sm:text-lg">
              No recycled material. Crafted to mirror the real exam in difficulty, structure, and reasoning depth.
            </p>
            <Button onClick={() => nav(V2_HUB)}
              className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-7 h-12 rounded-full">
              Begin Your Prep <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
export default IeltsLandingPage;
